import { DossierClientOperationName, SchemaWithMigrations, } from '@dossierhq/core';
import { useMemo, useRef } from 'react';
import { useSWRConfig } from 'swr';
import { assertIsDefined } from '../utils/AssertUtils.js';
import { clearCacheDueToSchemaMigrations, invalidateChangelogEvents, removeCacheEntity, updateCacheEntity, updateCacheEntityInfo, updateCacheSchemas, } from '../utils/CacheUtils.js';
export function useCachingDossierMiddleware() {
    const { cache, mutate } = useSWRConfig();
    const swrConfigRef = useRef({ cache, mutate });
    // TODO resolve eslint error
    // eslint-disable-next-line react-hooks/refs
    swrConfigRef.current = { cache, mutate };
    // TODO resolve eslint error
    // eslint-disable-next-line react-hooks/refs
    const middleware = useMemo(() => createCachingDossierMiddleware(swrConfigRef), []);
    return middleware;
}
function createCachingDossierMiddleware(swrConfig) {
    let lastSchemaVersion = 0;
    function handleUpdatedAdminSchema(context, mutate, schema) {
        let migrationsToApply = 0;
        for (const migration of schema.migrations) {
            if (migration.version > lastSchemaVersion) {
                migrationsToApply++;
            }
        }
        if (migrationsToApply > 0) {
            context.logger.info(`Clearing cache since there are new schema migrations (previous version=${lastSchemaVersion}, new version=${schema.version}, versions with migrations=${migrationsToApply})`);
            clearCacheDueToSchemaMigrations(mutate);
        }
        lastSchemaVersion = schema.version;
    }
    const middleware = async (context, operation) => {
        const result = await operation.next();
        if (result.isOk()) {
            assertIsDefined(swrConfig.current);
            const { cache, mutate } = swrConfig.current;
            switch (operation.name) {
                case DossierClientOperationName.archiveEntity: {
                    const payload = result.value;
                    updateCacheEntityInfo(mutate, payload);
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.createEntity: {
                    const payload = result.value;
                    updateCacheEntity(mutate, payload.entity);
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.deleteEntities: {
                    const [references] = operation.args;
                    references.forEach((it) => {
                        removeCacheEntity(mutate, it);
                    });
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.getSchemaSpecification: {
                    const args = operation.args;
                    const payload = result.value;
                    if (args[0]?.includeMigrations) {
                        handleUpdatedAdminSchema(context, mutate, payload);
                    }
                    break;
                }
                case DossierClientOperationName.publishEntities: {
                    const payload = result.value;
                    payload.forEach((it) => {
                        updateCacheEntityInfo(mutate, it);
                    });
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.unarchiveEntity: {
                    const payload = result.value;
                    updateCacheEntityInfo(mutate, payload);
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.unpublishEntities: {
                    const payload = result.value;
                    payload.forEach((it) => {
                        updateCacheEntityInfo(mutate, it);
                    });
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.updateEntity: {
                    const payload = result.value;
                    updateCacheEntity(mutate, payload.entity);
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.upsertEntity: {
                    const payload = result.value;
                    updateCacheEntity(mutate, payload.entity);
                    invalidateChangelogEvents(mutate);
                    break;
                }
                case DossierClientOperationName.updateSchemaSpecification: {
                    const args = operation.args;
                    const payload = result.value;
                    let schema;
                    if (args[1]?.includeMigrations) {
                        schema = new SchemaWithMigrations(payload.schemaSpecification);
                    }
                    updateCacheSchemas(cache, mutate, schema);
                    invalidateChangelogEvents(mutate);
                    break;
                }
            }
        }
        operation.resolve(result);
    };
    return middleware;
}
//# sourceMappingURL=useCachingDossierMiddleware.js.map